import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { HUMANIZE_PROVIDER, VCS_INTEGRATION_DETAILS_UNION } from "constants/vcsProviders";
import Box from "ds/components/Box";
import LinkButton from "ds/components/Button/LinkButton";
import Feedback from "ds/components/Feedback";
import { VcsIntegration, VcsProvider } from "types/generated";
import { getDocsUrl } from "utils/getDocsUrl";

import VCSIntegrationFeedbackWebhooksInfo from "./WebhooksInfo";
import {
  getVCSIntegrationIssue,
  VCS_INTEGRATION_ISSUE_MESSAGE,
  VCSIntegrationIssue,
} from "./helpers";

type VCSIntegrationFeedbackProps = {
  integration: VcsIntegration;
  viewPageMode?: boolean;
};

const VCSIntegrationFeedback = ({ integration, viewPageMode }: VCSIntegrationFeedbackProps) => {
  const issue = getVCSIntegrationIssue(integration);

  // FYI: GHE integration is deleted from GitHub
  if (issue === VCSIntegrationIssue.APP_DELETED) {
    return (
      <Feedback type={viewPageMode ? "callout" : "banner"} variant="danger">
        {VCS_INTEGRATION_ISSUE_MESSAGE[issue]}
      </Feedback>
    );
  }

  // FYI: Private key is probably invalid, they should update it
  if (issue === VCSIntegrationIssue.PRIVATE_KEY_INVALID) {
    return (
      <Feedback type={viewPageMode ? "callout" : "banner"} variant="danger">
        {VCS_INTEGRATION_ISSUE_MESSAGE[issue]}
      </Feedback>
    );
  }

  // FYI: GHE integration is not installed in GitHub
  if (
    issue === VCSIntegrationIssue.APP_NOT_INSTALLED &&
    integration.details?.__typename === VCS_INTEGRATION_DETAILS_UNION.GITHUB_ENTERPRISE_INTEGRATION
  ) {
    return (
      <Feedback type={viewPageMode ? "callout" : "banner"} variant="warning">
        {VCS_INTEGRATION_ISSUE_MESSAGE[issue]}
        <Box gap="large" padding="medium 0 0">
          <LinkButton
            variant="secondary"
            size="small"
            href={integration.details.appUrl}
            rel="noopener noreferrer"
            target="_blank"
          >
            Install the app
          </LinkButton>
          <ReadMoreDocsLink
            docsUrl={getDocsUrl(
              "/integrations/source-control/github#signed-in-with-another-option"
            )}
          />
        </Box>
      </Feedback>
    );
  }

  if (viewPageMode && integration.provider === VcsProvider.AzureDevops) {
    return (
      <VCSIntegrationFeedbackWebhooksInfo
        provider={HUMANIZE_PROVIDER[integration.provider]}
        docLink={getDocsUrl("/integrations/source-control/azure-devops#set-up-webhooks")}
      />
    );
  }

  if (viewPageMode && integration.provider === VcsProvider.Gitlab) {
    return (
      <VCSIntegrationFeedbackWebhooksInfo
        provider={HUMANIZE_PROVIDER[integration.provider]}
        docLink={getDocsUrl("/integrations/source-control/gitlab")}
      />
    );
  }

  if (viewPageMode && integration.provider === VcsProvider.BitbucketDatacenter) {
    return (
      <VCSIntegrationFeedbackWebhooksInfo
        provider={HUMANIZE_PROVIDER[integration.provider]}
        docLink={getDocsUrl(
          "/integrations/source-control/bitbucket-datacenter-server#create-the-bitbucket-data-center-integration"
        )}
      />
    );
  }

  if (viewPageMode && integration.provider === VcsProvider.BitbucketCloud) {
    return (
      <VCSIntegrationFeedbackWebhooksInfo
        provider={HUMANIZE_PROVIDER[integration.provider]}
        docLink={getDocsUrl("/integrations/source-control/bitbucket-cloud")}
      />
    );
  }

  return null;
};

export default VCSIntegrationFeedback;
